import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import { useTranslation } from 'react-i18next';

import { focusRing } from '@/components/ui/controlStyles';
import { ruleForBucket } from '@/components/ui/scoreScale';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

import { isRatingChoice, SCORE_VALUES, type RatingChoice } from './scoreOptions';

interface Props {
  value: RatingChoice;
  onValueChange: (next: RatingChoice) => void;
  /**
   * The id of the element whose text names this group — **the question itself** (§9: a score scale is a
   * labelled group, not five loose radios). A group named *"Score"* five times over says nothing about
   * which question is being scored, and the question is on screen anyway.
   */
  labelledBy: string;
}

/**
 * §3's score scale as a control: five large targets, each a **mono numeral with the coloured rule
 * beneath it**, arrow-key navigable, and every one of them labelled with its word.
 *
 * ⚠️ **It lives in this screen's folder rather than in `components/ui`, which is §4's rule** — *"anything
 * used on only one screen belongs in that screen's folder"* — and it is built on the Radix primitive
 * directly for the reason §4's own inventory implies: `components/ui/RadioGroup` renders a dot beside a
 * word, which is right for the four verdicts in the Overall block below and cannot render §3's scale at
 * all. The alternative was a render prop on the shared primitive for one caller, which is the drift §4
 * exists to prevent.
 *
 * ⚠️ **What is shared is the thing that must not be re-decided: the rules themselves.**
 * `ruleForBucket` is §3's table (`components/ui/scoreScale.ts`), so 1 and 5 are 2px and 2, 3 and 4 are
 * 1px here because they are there, and `fillingIn.test.tsx` asserts each option's rule classes equal
 * that table's. The numeral is always ink and 3 is neutral grey rather than amber — the expected
 * outcome, not a warning.
 *
 * ⚠️ **`Score` itself is deliberately not nested inside the radios.** It is a display component with
 * `role="img"` and an `aria-label` of its own, and a second accessible name inside a control is how a
 * radio ends up announced twice or announced wrong. The radio carries the label §9 fixes — *"3, meets
 * expectations"*, composed from the same `components.json` keys `Score` reads — and the numeral and
 * rule inside it are `aria-hidden`, because the word already says everything the colour does (§3:
 * colour never carries meaning alone).
 */
export function ScoreScale({ value, onValueChange, labelledBy }: Props) {
  const { t } = useTranslation('components');

  return (
    <RadixRadioGroup.Root
      aria-labelledby={labelledBy}
      className="flex flex-wrap gap-2"
      /*
       * Controlled from the form. `NOTHING_CHOSEN` matches no item, so an unanswered question has
       * nothing checked — and there is no *un-rate* option, deliberately: the API can set a rating and
       * cannot clear one (an explicit `null` is a 422), so offering to undo a choice would be offering
       * something no request could carry (`api/interviews.ts`).
       */
      value={value}
      onValueChange={(next) => {
        // A value this screen cannot name is one it does not store — the same guard the address parser
        // and the two `Select`s use. Radix can only emit the five, so this narrows rather than filters.
        if (isRatingChoice(next)) onValueChange(next);
      }}
    >
      {SCORE_VALUES.map((score) => (
        <RadixRadioGroup.Item
          key={score}
          value={String(score)}
          /* §9's labelled option, from `components.json`'s score words — never a bare number. */
          aria-label={t('score.announce', { value: score, label: t(`score.${score}`) })}
          className={cn(
            // A large target: 48px square, well past the 44px a finger and a shaky hand need, and the
            // reason the scale is a row of blocks rather than five dots in a line.
            'flex size-12 flex-col items-center justify-center gap-1.5',
            'rounded border border-rule-control bg-paper transition-motion',
            'hover:bg-paper-sunken',
            // Chosen: the accent boundary and its 8% wash, the same pair the active nav item uses.
            'data-[state=checked]:border-accent data-[state=checked]:bg-[hsl(var(--accent)/0.08)]',
            focusRing,
          )}
        >
          <span aria-hidden className={cn(type.numeral, 'text-base text-ink-900')}>
            {score}
          </span>
          {/* §3: the colour lives in a rule beneath the numeral, never in a filled chip. */}
          <span aria-hidden className={cn('w-6', ruleForBucket[score])} />
        </RadixRadioGroup.Item>
      ))}
    </RadixRadioGroup.Root>
  );
}
